import { motion } from "framer-motion";
import useScrollReveal from "../useScrollReveal";
import useSectionAnalytics, { mergeRefs } from "../useSectionAnalytics";
import { useTranslation } from "react-i18next";

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.15,
      duration: 0.6,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  }),
};

const accentVariants = {
  hidden: { scaleX: 0 },
  visible: (i) => ({
    scaleX: 1,
    transition: {
      delay: i * 0.15 + 0.35,
      duration: 0.45,
      ease: "easeOut",
    },
  }),
};

const iconWrapVariants = {
  hidden: { scale: 0, opacity: 0 },
  visible: (i) => ({
    scale: 1,
    opacity: 1,
    transition: {
      delay: i * 0.15 + 0.15,
      duration: 0.5,
      ease: [0.34, 1.56, 0.64, 1],
    },
  }),
};

export default function Features() {
  const [ref, visible] = useScrollReveal();
  const analyticsRef = useSectionAnalytics("features");
  const { t } = useTranslation();

  const cards = [
    {
      icon: "/icons/video-consultation.svg",
      title: t("features.video.title"),
      desc: t("features.video.desc"),
    },
    {
      icon: "/icons/e-prescription.svg",
      title: t("features.prescription.title"),
      desc: t("features.prescription.desc"),
    },
    {
      icon: "/icons/medical-records.svg",
      title: t("features.records.title"),
      desc: t("features.records.desc"),
    },
    {
      icon: "/icons/corporate-health.svg",
      title: t("features.corporate.title"),
      desc: t("features.corporate.desc"),
    },
  ];

  return (
    <section
      id="features"
      ref={mergeRefs(ref, analyticsRef)}
      className={`features-section section ${visible ? "visible" : ""}`}
    >
      <h2>{t("features.title")}</h2>

      <div className="features-grid">
        {cards.map((card, i) => (
          <motion.div
            key={i}
            className="feature-card"
            custom={i}
            initial="hidden"
            animate={visible ? "visible" : "hidden"}
            variants={cardVariants}
            whileHover={{
              y: -8,
              boxShadow: "0 20px 40px rgba(9,27,58,0.12)",
              borderColor: "var(--brand-primary)",
              transition: { duration: 0.3 },
            }}
          >
            <motion.span
              className="feature-card__accent"
              custom={i}
              variants={accentVariants}
            />

            <motion.div
              className="feature-card__icon-wrap"
              custom={i}
              variants={iconWrapVariants}
            >
              <motion.img
                src={card.icon}
                alt={card.title}
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300, damping: 15 }}
              />
            </motion.div>

            <h3>{card.title}</h3>
            <p>{card.desc}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
